/*
 * The vibe set picker's data (ENG-812, FX-6; backend ENG-811 and ENG-815).
 *
 * A vibe set is a skins-catalog item of type `vibe_set` whose `fxPackageId`
 * names the SongShake FX `vibe` package it plays. The listener's owned ones
 * are the shelf's `skins` of that type (`GET /me/skins/shelf?category=all`),
 * the free default SongShake Classic (source `default`) always among them,
 * and `equipped.vibe_set` is the one worn.
 *
 * Picking one is `PUT /me/skins/equipped/vibe_set` with `{ skinId }` (the
 * item id, not the package id). It is saved on the account, so the next
 * session's `GET /me/fx/loadout` (and `/me`'s `fxLoadout`) starts on it.
 * There is no server frame for the switch: after a 2xx the page calls
 * `fx.setVibe(fxPackageId)` itself (useVibeSetPicker).
 */
// Through the accessor every test mock of fetchWrapper.js provides.
import { useFetchWrapper as getFetchWrapper } from './fetchWrapper.js';
import config from '../config.js';

/** The equip slot (and item type) of a vibe set. */
export const VIBE_SET_SLOT = 'vibe_set';

const isId = (x) => typeof x === 'string' && x.trim().length > 0;

/** One owned shelf entry as a vibe set `{ id, name, fxPackageId, isDefault }`, or null when it is not one. */
export const normalizeVibeSet = (owned) => {
  const skin = owned?.skin;
  if (!skin || skin.type !== VIBE_SET_SLOT || !isId(skin.id) || !isId(skin.fxPackageId)) return null;
  return {
    id: skin.id,
    name: isId(skin.name) ? skin.name : skin.id,
    fxPackageId: skin.fxPackageId,
    isDefault: owned.source === 'default',
  };
};

/**
 * The vibe sets in a raw `SkinShelf` payload: `{ items, equippedId }`, the
 * default first and the rest in the shelf's order (newest first). With
 * nothing in the slot the default is the one worn.
 */
export const vibeSetsFromShelf = (shelf) => {
  const seen = new Set();
  const items = (Array.isArray(shelf?.skins) ? shelf.skins : [])
    .map(normalizeVibeSet)
    .filter((item) => item && !seen.has(item.id) && seen.add(item.id));
  items.sort((a, b) => Number(b.isDefault) - Number(a.isDefault));
  const slot = shelf?.equipped?.[VIBE_SET_SLOT];
  const equipped = items.find((item) => item.id === slot) ?? items.find((item) => item.isDefault) ?? null;
  return { items, equippedId: equipped?.id ?? null };
};

/** Reads the listener's vibe sets. Rejects with the fetchWrapper error (401 without a session). */
export const fetchVibeSets = async () => {
  const shelf = await getFetchWrapper().get(`${config.apiBaseUrl}/me/skins/shelf?category=all`);
  if (!shelf || !Array.isArray(shelf.skins)) {
    return Promise.reject({ status: 500, code: null, message: 'malformed skin shelf' });
  }
  return vibeSetsFromShelf(shelf);
};

/**
 * Wears the vibe set `skinId`. Rejects with 403 `skin_not_owned`, 404
 * `skin_not_found` or 400 `skin_type_mismatch` / `invalid_request`.
 */
export const equipVibeSet = (skinId) => {
  if (!isId(skinId)) return Promise.reject({ status: 400, code: 'invalid_request', message: 'missing skin id' });
  return getFetchWrapper().put(`${config.apiBaseUrl}/me/skins/equipped/${VIBE_SET_SLOT}`, { skinId });
};
